
import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PrimarySchedule } from './payroll/PrimarySchedule';
import { AdditionalSchedule } from './payroll/AdditionalSchedule';
import { AdditionalPayrollComponents } from './payroll/AdditionalPayrollComponents';
import type { Application } from '@/types/database.types';

interface PayrollScheduleProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const PayrollSchedule = ({ data, onSave }: PayrollScheduleProps) => {
  const { register, handleSubmit, reset, watch, setValue, control } = useForm<Partial<Application>>({
    defaultValues: {
      pay_schedule_type: data.pay_schedule_type || '',
      next_check_date: data.next_check_date || '',
      pay_period_start: data.pay_period_start || '',
      pay_period_end: data.pay_period_end || '',
      multiple_schedules_desc: data.multiple_schedules_desc || '',
      additional_schedules: data.additional_schedules || [],
      primary_full_time_count: data.primary_full_time_count || null,
      primary_part_time_count: data.primary_part_time_count || null,
      has_bonuses: data.has_bonuses || false,
      bonus_structure_desc: data.bonus_structure_desc || '',
      has_commission: data.has_commission || false,
      commission_structure_desc: data.commission_structure_desc || '',
      has_1099s: data.has_1099s || false,
      contractors_1099_desc: data.contractors_1099_desc || '',
      has_k1s: data.has_k1s || false,
      partners_k1_desc: data.partners_k1_desc || '',
      has_clergy_pay: data.has_clergy_pay || false,
      clergy_pay_desc: data.clergy_pay_desc || '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additional_schedules'
  });

  useEffect(() => {
    reset(data);
  }, [data, reset]);

  const onSubmit = handleSubmit((formData) => {
    onSave(formData);
  });

  const addSchedule = () => {
    append({
      schedule_type: '',
      pay_period_start: '',
      pay_period_end: '',
      next_check_date: '',
      description: '',
      full_time_count: null,
      part_time_count: null
    });
  };

  return (
    <form onChange={onSubmit} onSubmit={(e) => e.preventDefault()}>
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
            <h2 className="text-2xl font-semibold">Payroll Schedule</h2>
          </div>
          
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
            <p className="text-muted-foreground text-sm">
              Current payroll timing details facilitate seamless transition planning—allowing PEO providers to design implementation schedules that minimize disruption to existing operations.
            </p>
          </div>

          <div className="space-y-6">
            <PrimarySchedule 
              register={register} 
              watch={watch} 
              setValue={setValue}
              control={control}
            />
            
            <div className="space-y-4">
              {fields.map((field, index) => (
                <AdditionalSchedule
                  key={field.id}
                  index={index}
                  control={control}
                  register={register}
                  remove={remove}
                />
              ))}
            </div>

            <div className="flex justify-center">
              <Button type="button" onClick={addSchedule}>
                Add Additional Payroll Schedule
              </Button>
            </div>

            <AdditionalPayrollComponents 
              register={register} 
              setValue={setValue} 
              watch={watch}
            />
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default PayrollSchedule;
